import { ApiResponse } from 'apisauce';
import { ErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { ResponseModel } from '../../../shared/interfaces/response-model.interface';
import BlogMapper from '../../../shared/mappers/blog.mapper';
import { BlogModel } from '../../../shared/models/blog.model';
import api from '../../../shared/services/utils/api/api.service';
import errorHelper from '../../../shared/services/utils/api/error-helper.service';
import { BlogUiModel } from '../../../shared/ui-models/blog.ui-model';
import { getModuleName } from './blog.service';

function resolveBlog(
  response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>,
  displayName: string
): BlogUiModel | ErrorInfo {
  if (response.ok) {
    const { data } = response;
    if (data && data.data) {
      return BlogMapper.mapToUI(data.data);
    }
    return errorHelper(0, '', getModuleName(), displayName);
  }
  return errorHelper(
    Number(response.status),
    response.data && response.data.errorCode
      ? String(response.data.errorCode || '')
      : response.problem,
    getModuleName(),
    displayName
  );
}

export function getBlogs(
  offset: number,
  limit: number
): Promise<{ list: BlogUiModel[]; count: number } | ErrorInfo> {
  return api
    .getBlogs(offset, limit)
    .then((response: ApiResponse<ResponseModel<BlogModel[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return {
            list: BlogMapper.mapToUIList(data.data),
            count: data.count !== undefined ? data.count : data.data.length
          };
        }
        return errorHelper(0, '', getModuleName(), (api.getBlogs as any).displayName);
      }
      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : response.problem,
        getModuleName(),
        (api.getBlogs as any).displayName
      );
    });
}

export function getBlog(blogId: string): Promise<BlogUiModel | ErrorInfo> {
  return api
    .getBlog(blogId)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.getBlog as any).displayName)
    );
}

export function addNewBlog(blogData: BlogUiModel): Promise<BlogUiModel | ErrorInfo> {
  const mappedBlog = BlogMapper.mapToModel(blogData);
  return api
    .createBlog(mappedBlog)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.createBlog as any).displayName)
    );
}

export function updateBlog(
  blogId: string,
  blogData: BlogUiModel
): Promise<BlogUiModel | ErrorInfo> {
  const mappedBlog = BlogMapper.mapToModel(blogData);
  return api
    .updateBlog(blogId, mappedBlog)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.updateBlog as any).displayName)
    );
}

export function publishBlog(blogId: string): Promise<BlogUiModel | ErrorInfo> {
  return api
    .publishBlog(blogId)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.publishBlog as any).displayName)
    );
}

export function unpublishBlog(blogId: string): Promise<BlogUiModel | ErrorInfo> {
  return api
    .unpublishBlog(blogId)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.unpublishBlog as any).displayName)
    );
}

export function deleteBlog(blogId: string): Promise<{} | ErrorInfo> {
  return api.deleteBlog(blogId).then((response: ApiResponse<ResponseModel<{}>, ErrorInfo>) => {
    if (response.ok) {
      return {};
    }
    return errorHelper(
      Number(response.status),
      response.data && response.data.errorCode
        ? String(response.data.errorCode || '')
        : response.problem,
      getModuleName(),
      (api.deleteBlog as any).displayName
    );
  });
}

export function uploadBlogImage(file: File, blogId: string): Promise<BlogUiModel | ErrorInfo> {
  const data = new FormData();
  data.append('asset', file);
  data.append('blogId', blogId);

  return api
    .uploadBlogImage(data)
    .then((response: ApiResponse<ResponseModel<BlogModel>, ErrorInfo>) =>
      resolveBlog(response, (api.uploadBlogImage as any).displayName)
    );
}
